import { useState } from 'react'
import { toast } from 'sonner'
import { AlertTriangle, History, Loader2, Settings2, Trash2 } from 'lucide-react'
import {
  useClearWorkspaceContext,
  useSaveWorkspaceContext,
  useWorkspaceContext,
  useWorkspaceContextAudit,
} from '@/api/queries'
import { MAX_CONTEXT_CHARS } from '@/api/docfiller-types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ContextAuditTimeline } from '@/components/docfiller/ContextAuditTimeline'
import { SavedPromptsPicker } from '@/components/docfiller/SavedPromptsManager'

/**
 * Edit / review / clear the user's reusable workspace context — the
 * "project / workspace instructions" slice in the precedence stack. Saved
 * blob is snapshotted onto each document at upload time, so changes here
 * do not retroactively affect already-uploaded docs (by design — avoids
 * accidental stale-context drift when the user moves on to a new client).
 *
 * Surfaces a token-budget bar against ``MAX_CONTEXT_CHARS`` so the user
 * sees where they are vs the cap before submitting, a destructive
 * confirmation step before Clear, an audit timeline of recent
 * create/update/delete events, and a "read-only" UX when the caller
 * passes ``readOnly`` (e.g. when the current user lacks write access on
 * the workspace).
 */
export function WorkspaceContextDialog({
  triggerLabel,
  readOnly = false,
}: {
  triggerLabel?: string
  readOnly?: boolean
}) {
  const { data, isLoading } = useWorkspaceContext()
  const save = useSaveWorkspaceContext()
  const clear = useClearWorkspaceContext()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [confirmingClear, setConfirmingClear] = useState(false)
  const audit = useWorkspaceContextAudit({ enabled: open })

  const hasSaved = Boolean(data?.content)
  const triggerText = isLoading
    ? 'Loading context...'
    : triggerLabel ??
      (readOnly ? 'View context' : hasSaved ? 'Edit context' : 'Add context')
  const dirty = draft !== (data?.content ?? '')
  const charCount = draft.length
  const tooLong = charCount > MAX_CONTEXT_CHARS
  const percent = Math.min(
    100,
    Math.round((charCount / MAX_CONTEXT_CHARS) * 100),
  )
  const budgetTone: 'primary' | 'warning' | 'danger' = tooLong
    ? 'danger'
    : percent >= 80
      ? 'warning'
      : 'primary'

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraft(data?.content ?? '')
    } else {
      // Reset the destructive confirmation step on close so it does not
      // reappear when the dialog is re-opened later.
      setConfirmingClear(false)
    }
    setOpen(next)
  }

  const onSave = async () => {
    if (readOnly) return
    const content = draft.trim()
    if (!content) {
      toast.error('Context cannot be empty')
      return
    }
    if (content.length > MAX_CONTEXT_CHARS) {
      toast.error(
        `Trim to ${MAX_CONTEXT_CHARS.toLocaleString()} characters before saving`,
      )
      return
    }
    try {
      await save.mutateAsync({ content })
      toast.success('Saved workspace context')
      setOpen(false)
    } catch (e) {
      // Optimistic write was already rolled back by ``onError`` in the
      // mutation — refresh the draft from the cache so the user can retry.
      setDraft(data?.content ?? '')
      toast.error(e instanceof Error ? e.message : 'Save failed')
    }
  }

  const onClearAsk = () => {
    if (readOnly || !hasSaved) return
    setConfirmingClear(true)
  }

  const onClearConfirm = async () => {
    setConfirmingClear(false)
    try {
      await clear.mutateAsync()
      setDraft('')
      toast.success('Cleared workspace context')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Clear failed')
    }
  }

  const onInsertPrompt = (body: string) => {
    setDraft((prev) => {
      // Preserve the cursor-relative semantics by appending with a
      // separator if there's already content. ``next`` is then dirty
      // relative to the saved value, which is exactly what we want.
      const trimmed = prev.trimEnd()
      return trimmed ? `${trimmed}\n\n${body}` : body
    })
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="workspace-context-trigger"
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <Settings2 className="size-4" aria-hidden />
          )}
          {triggerText}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Reusable workspace context</DialogTitle>
          <DialogDescription>
            Project-level instructions the agent uses to pre-populate fields on
            new uploads. Changes here do not retroactively affect already-
            uploaded documents — edit a doc&apos;s context on its own page.
          </DialogDescription>
          {readOnly && (
            <p
              className="flex items-center gap-1.5 text-xs text-muted-foreground"
              data-testid="workspace-context-readonly-banner"
              role="status"
            >
              <AlertTriangle className="size-3.5" aria-hidden />
              Read-only — you do not have permission to modify the workspace
              context.
            </p>
          )}
        </DialogHeader>

        <Tabs defaultValue={readOnly ? 'view' : 'edit'}>
          <TabsList>
            {readOnly ? (
              <TabsTrigger
                value="view"
                data-testid="workspace-context-tab-view"
              >
                View
              </TabsTrigger>
            ) : (
              <TabsTrigger
                value="edit"
                data-testid="workspace-context-tab-edit"
              >
                Edit
              </TabsTrigger>
            )}
            <TabsTrigger
              value="audit"
              data-testid="workspace-context-tab-audit"
            >
              <History className="size-3.5" /> Audit
            </TabsTrigger>
          </TabsList>

          {readOnly ? (
            <TabsContent value="view" className="space-y-3">
              <ReadOnlyContextView
                content={data?.content ?? ''}
                isLoading={isLoading}
              />
            </TabsContent>
          ) : (
            <TabsContent value="edit" className="space-y-3">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="workspace-context-textarea">Context</Label>
                  <SavedPromptsPicker onInsert={onInsertPrompt} />
                </div>
                <Textarea
                  id="workspace-context-textarea"
                  data-testid="workspace-context-textarea"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Example: My company is Acme Corp, EIN 12-3456789. My address is 1 Infinite Loop, Cupertino CA 95014. My phone is 555-867-5309."
                  rows={10}
                  className="resize-y"
                  disabled={
                    isLoading || save.isPending || clear.isPending
                  }
                  aria-invalid={tooLong || undefined}
                />
                <BudgetMeter
                  chars={charCount}
                  max={MAX_CONTEXT_CHARS}
                  tone={budgetTone}
                  tooLong={tooLong}
                />
              </div>

              <DialogFooter className="gap-2 sm:justify-between">
                {confirmingClear ? (
                  <DestructiveConfirm
                    onCancel={() => setConfirmingClear(false)}
                    onConfirm={onClearConfirm}
                    pending={clear.isPending}
                  />
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClearAsk}
                    disabled={
                      !hasSaved || clear.isPending || save.isPending
                    }
                    data-testid="workspace-context-clear"
                  >
                    {clear.isPending && (
                      <Loader2 className="size-4 animate-spin" />
                    )}
                    <Trash2 className="size-3.5" />
                    Clear saved context
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={onSave}
                  disabled={
                    !dirty ||
                    tooLong ||
                    save.isPending ||
                    isLoading ||
                    charCount === 0
                  }
                  data-testid="workspace-context-save"
                >
                  {save.isPending && (
                    <Loader2 className="size-4 animate-spin" />
                  )}
                  Save
                </Button>
              </DialogFooter>
            </TabsContent>
          )}

          <TabsContent value="audit" className="space-y-2">
            <ContextAuditTimeline
              events={audit.data ?? []}
              isLoading={audit.isLoading}
              error={audit.error}
              testid="workspace-context-audit-timeline"
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

function ReadOnlyContextView({
  content,
  isLoading,
}: {
  content: string
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <p
        className="text-xs text-muted-foreground"
        data-testid="workspace-context-readonly-loading"
      >
        Loading saved context…
      </p>
    )
  }
  if (!content) {
    return (
      <p
        className="text-xs text-muted-foreground"
        data-testid="workspace-context-readonly-empty"
      >
        No workspace context saved yet.
      </p>
    )
  }
  return (
    <pre
      data-testid="workspace-context-readonly-view"
      className="max-h-72 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-border bg-card p-2 font-sans text-xs leading-relaxed text-foreground/90"
    >
      {content}
    </pre>
  )
}

function BudgetMeter({
  chars,
  max,
  tone,
  tooLong,
}: {
  chars: number
  max: number
  tone: 'primary' | 'warning' | 'danger'
  tooLong: boolean
}) {
  const percent = Math.min(100, Math.round((chars / max) * 100))
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span
          data-testid="workspace-context-charcount"
          className={tooLong ? 'text-destructive' : undefined}
        >
          {chars.toLocaleString()} / {max.toLocaleString()} characters
        </span>
        {tooLong && (
          <Badge tone="danger" size="xs">
            Too long
          </Badge>
        )}
      </div>
      <Progress
        percent={percent}
        size="sm"
        tone={tone}
        data-testid="workspace-context-budget-bar"
      />
    </div>
  )
}

function DestructiveConfirm({
  onCancel,
  onConfirm,
  pending,
}: {
  onCancel: () => void
  onConfirm: () => void | Promise<void>
  pending: boolean
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive-soft/40 px-3 py-2 text-xs text-destructive-soft-foreground"
      role="alertdialog"
      aria-label="Confirm clear saved context"
      data-testid="workspace-context-clear-confirm"
    >
      <span className="font-medium">
        Clear the saved context? This cannot be undone.
      </span>
      <div className="ml-auto flex gap-1.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={pending}
          data-testid="workspace-context-clear-cancel"
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={onConfirm}
          disabled={pending}
          data-testid="workspace-context-clear-confirm-button"
        >
          {pending && <Loader2 className="size-3 animate-spin" />}
          Clear
        </Button>
      </div>
    </div>
  )
}
